// Solid, high-contrast HUD control button for the persistent in-game controls
// (settings / pause / leave / sound). Unlike ModalButton, this reads as a
// physical, chunky "key": a solid saturated base in a cool tone (so it
// separates from the warm wood background) plus a hard darker bottom edge that
// collapses on press for a tactile feel.
import classNames from "classnames"
import React from "react"

export type ControlAccent = "violet" | "cyan" | "sky" | "rose" | "amber" | "emerald"

type GameControlButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  accent?: ControlAccent
}

// top/bottom: the solid face gradient. edge: the hard 3D bottom lip. glow: the
// hover bloom. All cool, deep tones — warm colors would merge with the wood.
const ACCENTS: Record<ControlAccent, { top: string; bottom: string; edge: string; glow: string }> = {
  violet: { top: "#8b5cf6", bottom: "#6d28d9", edge: "#4c1d95", glow: "rgba(139,92,246,0.5)" },
  cyan: { top: "#22d3ee", bottom: "#0891b2", edge: "#155e75", glow: "rgba(34,211,238,0.5)" },
  sky: { top: "#38bdf8", bottom: "#0284c7", edge: "#075985", glow: "rgba(56,189,248,0.5)" },
  rose: { top: "#f43f5e", bottom: "#be123c", edge: "#881337", glow: "rgba(244,63,94,0.5)" },
  amber: { top: "#fbbf24", bottom: "#d97706", edge: "#92400e", glow: "rgba(251,191,36,0.5)" },
  emerald: { top: "#34d399", bottom: "#059669", edge: "#065f46", glow: "rgba(52,211,153,0.5)" },
}

const GameControlButton: React.FC<GameControlButtonProps> = ({
  children,
  className,
  accent = "sky",
  disabled,
  ...rest
}) => {
  const a = ACCENTS[accent]

  return (
    <button
      {...rest}
      disabled={disabled}
      style={{
        ["--edge" as any]: a.edge,
        ["--glow" as any]: a.glow,
        backgroundImage: `linear-gradient(to bottom, ${a.top}, ${a.bottom})`,
      }}
      className={classNames(
        "group relative inline-flex items-center justify-center gap-2 overflow-hidden select-none",
        "rounded-xl border border-white/20 px-4 py-2.5 font-poppins font-semibold text-white",
        "transition-[transform,box-shadow,filter,border-color] duration-150 ease-out",
        // Resting: a hard 4px bottom "edge" gives the key real height, plus an
        // ambient drop shadow and an inset top highlight (bevel).
        "shadow-[0_4px_0_0_var(--edge),0_10px_22px_-6px_rgba(0,0,0,0.5),inset_0_1px_0_0_rgba(255,255,255,0.30)]",
        // Hover: lift toward the viewer, brighten slightly, bloom a soft glow.
        "hover:-translate-y-[2px] hover:brightness-110 hover:border-white/30 hover:shadow-[0_4px_0_0_var(--edge),0_14px_30px_-6px_var(--glow),inset_0_1px_0_0_rgba(255,255,255,0.35)]",
        // Active: press down onto the edge so it collapses — the tactile click.
        "active:translate-y-[4px] active:brightness-95 active:shadow-[0_0_0_0_var(--edge),0_3px_10px_-4px_rgba(0,0,0,0.5),inset_0_1px_0_0_rgba(255,255,255,0.20)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60",
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:grayscale disabled:translate-y-0",
        className
      )}
    >
      {/* Glossy top bevel */}
      <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-xl bg-gradient-to-b from-white/20 to-transparent" />
      <span
        className="relative z-10 flex h-full w-full items-center justify-center gap-2"
        style={{ textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}
      >
        {children}
      </span>
    </button>
  )
}

export default GameControlButton
